import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { getPostBySlug, posts } from "./blog-data";
import { usePageMeta } from "../hooks/usePageMeta";

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  usePageMeta({
    title: post?.title || "Article",
    description: post?.summary || "",
    path: post ? `/blog/${post.slug}` : "/blog",
  });
  if (!post) return <Navigate to="/blog" replace />;

  const related = posts.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-[#FDFCF8] text-[#2D332F]">
      <header className="border-b border-black/5 bg-[#FDFCF8]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <Link
            to="/"
            data-testid="post-back-home"
            className="flex items-center"
          >
            <img
              src="/logo.jpg"
              alt="Neurological Communication Partners"
              className="h-14 w-auto rounded-xl ring-1 ring-black/5"
            />
          </Link>
          <Link
            to="/blog"
            data-testid="post-back-blog"
            className="inline-flex items-center gap-2 text-sm text-[#5C6661] hover:text-[#2D332F] transition"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> All articles
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 md:px-10 py-14 md:py-20">
        <p className="text-xs uppercase tracking-[0.22em] text-[#739072] mb-4">
          {post.category} · {post.readTime}
        </p>
        <h1
          data-testid="post-title"
          className="font-serif text-4xl md:text-5xl font-light tracking-tight leading-[1.08]"
        >
          {post.title}
        </h1>
        <p className="mt-6 text-[#5C6661] text-lg leading-relaxed">
          {post.summary}
        </p>

        <article
          data-testid="post-body"
          className="prose-content mt-12 space-y-6 text-[#2D332F]/85 text-base md:text-lg leading-relaxed"
        >
          {post.body.map((para, i) => (
            <p key={i} className="whitespace-pre-line">
              {para}
            </p>
          ))}
        </article>

        <div className="mt-16 rounded-3xl border border-black/5 bg-[#F3F1E7] p-7 md:p-8">
          <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-3">
            Next step
          </p>
          <p className="font-serif text-2xl text-[#2D332F] mb-4 leading-snug">
            Want to talk this through with Kathleen?
          </p>
          <Link
            to="/#contact"
            data-testid="post-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#2D332F] text-[#FDFCF8] text-sm hover:bg-[#739072] transition"
          >
            Request a consult
            <ArrowUpRight size={14} strokeWidth={1.5} />
          </Link>
        </div>

        {related.length > 0 && (
          <div className="mt-16 border-t border-black/10 pt-10">
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-6">
              Keep reading
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  data-testid={`post-related-${r.slug}`}
                  className="block rounded-2xl border border-black/5 p-5 hover:bg-[#F3F1E7] transition"
                >
                  <p className="text-xs uppercase tracking-[0.18em] text-[#739072] mb-2">
                    {r.category}
                  </p>
                  <p className="font-serif text-lg text-[#2D332F] leading-snug">
                    {r.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
